import React, { useState, useCallback, useMemo } from 'react';
import { FaCreditCard, FaMobile, FaShieldAlt, FaCheckCircle, FaTimes, FaEdit } from 'react-icons/fa';

// Simple phone number dialog component
const PhoneNumberDialog = ({ isOpen, onClose, onSave, currentPhone = '' }) => {
  const [phoneNumber, setPhoneNumber] = useState(currentPhone);
  const [countryCode, setCountryCode] = useState('+254');

  const handleSave = () => {
    if (phoneNumber && phoneNumber.length >= 9) {
      onSave({ phoneNumber, countryCode, fullNumber: `${countryCode}${phoneNumber}` });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center">
              <FaMobile className="mr-2 text-green-600" />
              M-Pesa Phone Number
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FaTimes />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select 
                value={countryCode} 
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="+254">🇰🇪 Kenya (+254)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  {countryCode}
                </span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="712345678/112345678"
                  maxLength="9"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              {/* <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button> */}
              <button
                onClick={handleSave}
                disabled={!phoneNumber || phoneNumber.length < 9}
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Confirm Phonenumber
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple card details dialog
const CardDetailsDialog = ({ isOpen, onClose, onSave, currentCard = {} }) => {
  const [cardNumber, setCardNumber] = useState(currentCard.cardNumber || '');
  const [expiryDate, setExpiryDate] = useState(currentCard.expiryDate || '');
  const [cvv, setCvv] = useState(currentCard.cvv || '');
  const [cardholderName, setCardholderName] = useState(currentCard.cardholderName || '');

  const handleSave = () => {
    if (cardNumber && expiryDate && cvv && cardholderName) {
      onSave({ cardNumber, expiryDate, cvv, cardholderName });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center">
              <FaCreditCard className="mr-2 text-blue-600" />
              Card Details
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FaTimes />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
              <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
              <input
                type="text"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="1234 5678 9012 3456"
                maxLength="16"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                <input
                  type="text"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  placeholder="MM/YY"
                  maxLength="5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                <input
                  type="text"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                  placeholder="123"
                  maxLength="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!cardNumber || !expiryDate || !cvv || !cardholderName}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentMethodSection = ({ 
  paymentMethods, 
  selectedPaymentMethod, 
  setSelectedPaymentMethod, 
  handleSelectPaymentMethod: onSelectPaymentMethodFromParent, // Rename to avoid conflict
  setOrderData,
  orderData, 
  subTotal, 
  shippingCost 
}) => {
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [phoneDetails, setPhoneDetails] = useState(null);
  const [cardDetails, setCardDetails] = useState(null);

  // Enhanced payment method information
  const paymentMethodInfo = useMemo(() => ({
    1: { 
      name: "M-Pesa", 
      description: "Pay using your mobile money",
      features: ["Instant processing", "No additional fees", "Secure mobile payment"],
      acceptedCards: ["Safaricom M-Pesa", "Airtel Money"]
    },
    2: { 
      name: "Credit/Debit Card", 
      description: "Pay with Visa, Mastercard, or other cards",
      features: ["Worldwide accepted", "Secure encryption", "Instant verification"],
      acceptedCards: ["Visa", "Mastercard", "American Express"]
    }
  }), []);

  const handleSelectPaymentMethod = useCallback((methodId) => {
    // Call the parent's handleSelectPaymentMethod to set isPaymentMethodSelected
    if (onSelectPaymentMethodFromParent) {
      onSelectPaymentMethodFromParent(methodId);
    }
    
    setSelectedPaymentMethod(methodId);
    
    // Store payment method selection in order data
    setOrderData(prev => ({
      ...prev,
      paymentMethod: methodId,
      paymentMethodName: paymentMethods.find(m => m.id === methodId)?.name
    }));
    
    // Open appropriate configuration dialog
    if (methodId === 1) {
      setShowPhoneDialog(true);
    } else if (methodId === 2) {
      setShowCardDialog(true);
    }
  }, [onSelectPaymentMethodFromParent, setSelectedPaymentMethod, setOrderData, paymentMethods]);

  const handlePhoneSave = useCallback((phone) => {
    setPhoneDetails(phone);
    setOrderData(prev => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        phoneNumber: phone.fullNumber,
        countryCode: phone.countryCode
      }
    }));
  }, [setOrderData]);

  const handleCardSave = useCallback((card) => {
    setCardDetails(card);
    setOrderData(prev => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        cardNumber: `****-****-****-${card.cardNumber.slice(-4)}`,
        cardholderName: card.cardholderName
      }
    }));
  }, [setOrderData]);

  const handleChangePaymentMethod = useCallback(() => {
    setSelectedPaymentMethod(null);
    setPhoneDetails(null);
    setCardDetails(null);
    setOrderData(prev => ({
      ...prev,
      paymentMethod: null,
      paymentMethodName: null,
      paymentDetails: {}
    }));
  }, [setSelectedPaymentMethod, setOrderData]);

  // const totalAmount = subTotal + shippingCost;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100 animate-fade-in">
      {/* Header with security badge */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <span className="bg-orange-100 text-orange-600 rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold mr-3">
            3
          </span>
          Payment Method
        </h2>
        <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full">
          <FaShieldAlt className="mr-2" />
          <span className="text-sm font-medium">256-bit SSL Secured</span>
        </div>
      </div>

      {/* Payment amount summary */}
      {/* <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total amount to pay:</span>
          <span className="text-2xl font-bold text-orange-600">
            KSH {totalAmount.toLocaleString('en-KE')}
          </span>
        </div>
      </div> */}

      {selectedPaymentMethod ? (
        <div className="space-y-6">
          {/* Selected Payment Method Display */}
          {paymentMethods
            .filter(method => method.id === selectedPaymentMethod)
            .map(method => {
              
              return (
                <div key={method.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white border border-green-300 rounded-lg">
                        <img 
                          src={method.logo} 
                          alt={method.name} 
                          className="h-6 w-6 object-contain"
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-green-800">{method.name}</p>
                        <p className="text-sm text-green-600">
                          {selectedPaymentMethod === 1 && phoneDetails ? (
                            `Phone: ${phoneDetails.fullNumber}`
                          ) : selectedPaymentMethod === 2 && cardDetails ? (
                            `Card: ${cardDetails.cardNumber.slice(-4)}`
                          ) : (
                            "Configuration required"
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {((selectedPaymentMethod === 1 && phoneDetails) || (selectedPaymentMethod === 2 && cardDetails)) && (
                        <FaCheckCircle className="text-green-600" />
                      )}
                      <button
                        onClick={() => {
                          if (selectedPaymentMethod === 1) {
                            setShowPhoneDialog(true);
                          } else if (selectedPaymentMethod === 2) {
                            setShowCardDialog(true);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-all duration-200 hover:underline flex items-center gap-1"
                      >
                        <FaEdit className="text-xs" />
                        {((selectedPaymentMethod === 1 && phoneDetails) || (selectedPaymentMethod === 2 && cardDetails)) ? 'Edit' : 'Configure'}
                      </button>
                      <button
                        onClick={handleChangePaymentMethod}
                        className="text-red-600 hover:text-red-800 text-sm font-medium transition-all duration-200 hover:underline ml-2"
                      >
                        Change Method
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600 mb-6">Choose your preferred payment method:</p>
          
          <div className="space-y-3">
            {paymentMethods.map((method) => {
              const methodInfo = paymentMethodInfo[method.id];
              
              return (
                <label key={method.id} 
                       className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 cursor-pointer group">
                  <div className="flex items-start mt-1">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      onChange={() => handleSelectPaymentMethod(method.id)}
                      className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500 focus:ring-2"
                    />
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-50 group-hover:bg-orange-100 rounded-lg transition-all duration-200 mt-1">
                          <img 
                            src={method.logo} 
                            alt={method.name} 
                            className="h-5 w-5 object-contain"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-base text-gray-800 group-hover:text-orange-600 transition-colors">
                            {method.name}
                          </h3>
                          <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                            {methodInfo?.description}
                          </p>
                          
                          {/* Additional payment method details */}
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <FaShieldAlt className="text-green-500" />
                              <span>Secure & encrypted transaction</span>
                            </div>
                            
                            {method.id === 1 && (
                              <div className="text-xs text-gray-500">
                                • Instant payment confirmation
                                • SMS notifications included
                                • Available 24/7
                              </div>
                            )}
                            
                            {method.id === 2 && (
                              <div className="text-xs text-gray-500">
                                • Accepts Visa, MasterCard, American Express
                                • 3D Secure authentication
                                • International cards supported
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <img 
                        src={method.logo} 
                        alt={method.name} 
                        className="h-6 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity ml-4"
                      />
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Dialogs */}
      <PhoneNumberDialog 
        isOpen={showPhoneDialog}
        onClose={() => setShowPhoneDialog(false)}
        onSave={handlePhoneSave}
        currentPhone={phoneDetails?.phoneNumber || ''}
      />
      
      <CardDetailsDialog 
        isOpen={showCardDialog}
        onClose={() => setShowCardDialog(false)}
        onSave={handleCardSave}
        currentCard={cardDetails || {}}
      />
    </div>
  );
};

export default PaymentMethodSection;